import type { ColumnFiltersState } from '@tanstack/table-core';
import type { ReactNode } from 'react';
import {
  Button,
  colors,
  ControlGroup,
  darkThemeSelector,
  Divider,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuPopover,
  HorizontalScrollShadow,
  HStack,
  Icon,
  sizing,
  styled,
  Text,
  useHorizontalShadowOnScroll,
} from '@meterup/atto';
import { useMeasure } from '@uidotdev/usehooks';
import { isNumber } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  FilterComponentFunction,
  FilterComponentProps,
  FilterOption,
  FilterOptions,
  FilterValue,
  FilterVariant,
  NormalizedFilterOption,
  SharedColumnDef,
} from '../createColumnBuilder';
import type { AutoCollectionStateInterface } from '../utils';
import { sortingCollator } from '../../../helpers/utils/strings';
import { parseDateTime } from '../../../topics/time';
import { DateTimeRangeStorageProvider } from '../../DateTimeRangePicker/DateTimeRangeStorageContext';
import { FormatDateTime } from '../../FormatDateTime';
import { KeyBoundSearchInput } from '../../KeyBoundSearchInput';
import { TableColumnsEnhanced } from '../TableColumns/TableColumns';
import {
  TableFiltersFieldDateRange,
  TableFiltersFieldMultiSelect,
  TableFiltersFieldNumber,
  TableFiltersFieldNumberRange,
  TableFiltersFieldSelect,
  TableFiltersFieldText,
} from './TableFiltersField/TableFiltersField';
import { TableFiltersToken } from './TableFiltersToken';
import { isFilterApplied } from './utils';

type SelectedFiltersDropdownProps = {
  options: Array<{ value: string; label: string; group?: string | null }>;
  selectedFilters: string[];
  setSelectedFilters: (filters: string[]) => void;
  groupOrder?: Map<string, number>;
};

const DEFAULT_FILTER_GROUP_NAME = 'Filters';

function DisplayForFilterValue({
  value,
  options,
}: {
  value: FilterValue;
  options?: FilterOption[];
}): ReactNode {
  if (!value) {
    return 'All';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'All';
    }

    if (options) {
      return (
        <HStack spacing={4}>
          {value.map((v, index) => (
            <HStack key={v} wrap="nowrap" align="center">
              {options.find((opt) => opt.value === v)?.label ?? v}
              {index < value.length - 1 ? <Text>,</Text> : null}
            </HStack>
          ))}
        </HStack>
      );
    }

    if (value[0] === null && value[1] === null) {
      return null;
    }
    if (isNumber(value[0]) || isNumber(value[1])) {
      return (
        <HStack align="center" spacing={4} wrap="nowrap">
          {value[0]}
          <Icon icon="arrow-right" size={10} />
          {value[1]}
        </HStack>
      );
    }

    const startDate = parseDateTime(value[0]);
    const endDate = parseDateTime(value[1]);
    if (startDate || endDate) {
      return (
        <HStack align="center" spacing={4} wrap="nowrap">
          <FormatDateTime value={startDate} />
          <Icon icon="arrow-right" size={10} />
          <FormatDateTime value={endDate} />
        </HStack>
      );
    }

    return value.join(', ');
  }

  if (options) {
    return options.find((opt) => opt.value === value)?.label;
  }

  return value;
}

function SelectedFiltersDropdown({
  options,
  selectedFilters,
  setSelectedFilters,
  groupOrder,
}: SelectedFiltersDropdownProps) {
  const handleOnSelect = useCallback(
    (val: string) => {
      let newVal = [...selectedFilters];
      if (selectedFilters.includes(val)) {
        newVal = selectedFilters.filter((selected) => selected !== val);
      } else {
        newVal = [...selectedFilters, val];
      }
      setSelectedFilters(newVal);
    },
    [selectedFilters, setSelectedFilters],
  );

  const groupedOptions = useMemo(() => {
    const groups = new Map<string, Array<{ value: string; label: string }>>();

    for (const opt of options) {
      const groupName = opt.group ?? DEFAULT_FILTER_GROUP_NAME;
      let arr = groups.get(groupName);
      if (!arr) {
        arr = [];
        groups.set(groupName, arr);
      }
      arr.push({ value: opt.value, label: opt.label });
    }

    return Array.from(groups.entries()).sort(([groupA], [groupB]) => {
      if (groupA === DEFAULT_FILTER_GROUP_NAME && groupB !== DEFAULT_FILTER_GROUP_NAME) return -1;
      if (groupA !== DEFAULT_FILTER_GROUP_NAME && groupB === DEFAULT_FILTER_GROUP_NAME) return 1;

      // If groupOrder is provided, sort by column order; otherwise fall back to alphabetical
      if (groupOrder) {
        const orderA = groupOrder.get(groupA) ?? Infinity;
        const orderB = groupOrder.get(groupB) ?? Infinity;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
      }

      return groupA.localeCompare(groupB);
    });
  }, [options, groupOrder]);

  return (
    <DropdownMenu>
      <DropdownMenuButton tabIndex={0} arrangement="hidden-label" icon="filter" size="small">
        Add a filter...
      </DropdownMenuButton>
      <DropdownMenuPopover align="start">
        {groupedOptions.map(([groupName, groupOptions]) => (
          <DropdownMenuGroup key={groupName} label={groupName}>
            {groupOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selectedFilters.includes(opt.value)}
                onSelect={() => handleOnSelect(opt.value)}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuGroup>
        ))}
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}

const TableFiltersDivider = styled(Divider, {
  padding: '$4 $2',
});
TableFiltersDivider.displayName = 'TableFiltersDivider';

const TableFiltersViews = styled(ControlGroup, {
  gridArea: 'views',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
  padding: `$10 $4 $10 ${sizing.sides}`,
});
TableFiltersViews.displayName = 'TableFiltersViews';

const TableFiltersOptionsScroll = styled(HorizontalScrollShadow, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
  maxWidth: '100%',
  padding: `$10 ${sizing.sides}`,

  variants: {
    hasControls: {
      true: {},
      false: {},
    },
    hasViews: {
      true: {},
      false: {},
    },
    isNarrow: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      hasControls: true,
      isNarrow: false,
      css: {
        paddingRight: '$4',
      },
    },
    {
      hasViews: true,
      isNarrow: false,
      css: {
        paddingLeft: '$4',
      },
    },
  ],
});
TableFiltersOptionsScroll.displayName = 'TableFiltersOptionsScroll';

const TableFiltersOptions = styled('div', {
  gridArea: 'options',
  position: 'relative',
  maxWidth: '100%',
  minWidth: 0,

  '&::before': {
    content: '',
    position: 'absolute',
    top: '-0.5px',
    right: `${sizing.sides}`,
    left: `${sizing.sides}`,
    height: '$1',
    background: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      background: colors.strokeNeutralDark,
    },
  },
});
TableFiltersOptions.displayName = 'TableFiltersOptions';

const TableFiltersControls = styled(ControlGroup, {
  gridArea: 'controls',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '$8',
  padding: `$10 ${sizing.sides} $10 $8`,
});
TableFiltersControls.displayName = 'TableFiltersControls';

const TableFiltersContainer = styled('div', {
  display: 'grid',
  width: '100%',

  variants: {
    isNarrow: {
      true: {
        gridTemplateColumns: 'min-content 1fr min-content',
        gridTemplateRows: "'min-content' 'min-content'",
        gridTemplateAreas: `
          "views blank controls"
          "options options options"
        `,
      },
      false: {
        gridTemplateColumns: 'min-content 1fr min-content',
        gridTemplateAreas: `
          "views options controls"
        `,
        width: '100%',
      },
    },
  },
});
TableFiltersContainer.displayName = 'TableFiltersContainer';

type FilterMapVal<RowType> = Omit<
  FilterComponentProps<RowType>,
  'handleFiltersChange' | 'onKeyDown'
> & {
  label: string;
  filter?: FilterComponentFunction<RowType>;
  variant?: FilterVariant;
  options?: FilterOptions<RowType>;
};

function TableFilter<RowType>({
  mappedFilter,
  options,
  handleFiltersChange,
  handleFilterValueSelected,
}: {
  mappedFilter: FilterMapVal<RowType>;
  options: FilterOption[] | undefined;
  handleFiltersChange: (id: string, value: FilterValue) => void;
  handleFilterValueSelected?: (id: string) => void;
}) {
  if (mappedFilter?.filter) {
    return mappedFilter.filter({
      ...mappedFilter,
      handleFiltersChange,
      onKeyDown: () => {},
    });
  }

  switch (mappedFilter.variant) {
    case 'select':
      return (
        <TableFiltersFieldSelect
          {...mappedFilter}
          options={options!}
          value={mappedFilter.value as string}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'multi-select':
      return (
        <TableFiltersFieldMultiSelect
          {...mappedFilter}
          options={options!}
          value={mappedFilter.value as string[]}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
        />
      );
    case 'number-range':
      return (
        <TableFiltersFieldNumberRange
          {...mappedFilter}
          value={mappedFilter.value as [number | null, number | null]}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'number':
      return (
        <TableFiltersFieldNumber
          {...mappedFilter}
          value={mappedFilter.value as number | null}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'text':
      return (
        <TableFiltersFieldText
          {...mappedFilter}
          value={mappedFilter.value as string}
          onChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'date-range':
      return (
        <DateTimeRangeStorageProvider>
          <TableFiltersFieldDateRange
            {...mappedFilter}
            value={mappedFilter.value as [string | null, string | null]}
            handleFiltersChange={handleFiltersChange}
            onKeyDown={() => {}}
            onValueSelected={
              handleFilterValueSelected
                ? () => handleFilterValueSelected(mappedFilter.id)
                : undefined
            }
          />
        </DateTimeRangeStorageProvider>
      );
    default:
      return null;
  }
}

export function TableFilters<RowType>({
  columns = [],
  data,
  filtersState = [],
  onFiltersChange = () => {},
  onClearFilters,
  controls,
  staticFilters,
}: {
  columns?: SharedColumnDef<RowType, any>[];
  data?: RowType[];
  filtersState?: ColumnFiltersState;
  onFiltersChange?: (columnState: ColumnFiltersState) => void;
  onClearFilters?: React.MouseEventHandler<HTMLButtonElement>;
  controls?: ReactNode;
  staticFilters?: ReactNode;
}) {
  const { getBoxShadow, onScrollHandler, handleTargetChange } = useHorizontalShadowOnScroll('both');
  const optionsRef = useRef<HTMLDivElement>(null);
  const [openFilters, setOpenFilters] = useState<Set<string>>(new Set());

  const handleResizeOptions = useCallback(() => {
    if (optionsRef.current) {
      handleTargetChange(optionsRef.current);
    }
  }, [handleTargetChange]);

  useEffect(() => {
    handleResizeOptions();
    window.addEventListener('resize', handleResizeOptions);
    return () => {
      window.removeEventListener('resize', handleResizeOptions);
    };
  }, [handleResizeOptions]);

  const filters: FilterMapVal<RowType>[] = useMemo(() => {
    const filterList: FilterMapVal<RowType>[] = [];

    for (const col of columns) {
      // Add main column filter if it has filter configuration
      if (col && (col.filterFn || col.filter) && col.filterVariant) {
        const label = (col.filterLabel ?? col.header) as string;
        const value = filtersState?.find((filter) => filter.id === col.id)?.value as FilterValue;

        filterList.push({
          id: col.id,
          value,
          label,
          data,
          filter: col.filter,
          options: col.filterOptions,
          variant: col.filterVariant,
        });
      }

      // Add subline filter if column has subline and sublineFilterVariant is defined
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter)
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = col.filterLabel ?? col.header;
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const value = filtersState?.find((filter) => filter.id === sublineId)?.value as FilterValue;

        filterList.push({
          id: sublineId,
          value,
          label,
          data,
          filter: col.sublineFilter,
          options: col.sublineFilterOptions,
          variant: col.sublineFilterVariant,
        });
      }
    }

    return filterList;
  }, [columns, filtersState, data]);

  const filtersMap = useMemo(() => {
    const map = new Map<string, FilterMapVal<RowType>>();
    for (const f of filters) {
      map.set(f.id, f);
    }
    return map;
  }, [filters]);

  const filterOptionsResult = useMemo(() => {
    const optionsMap = new Map<string, { value: string; label: string; group?: string | null }>();
    const groupOrderMap = new Map<string, number>();
    let columnIndex = 0;

    for (const col of columns) {
      // Add main column filter option
      if ((col?.filterFn || col?.filter) && col?.filterVariant && col.id) {
        const label = (col.filterLabel ?? col.header) as string;
        const group = col.meta?.group ?? null;
        const groupName = group ?? DEFAULT_FILTER_GROUP_NAME;

        // Track the first occurrence of each group
        if (!groupOrderMap.has(groupName)) {
          groupOrderMap.set(groupName, columnIndex);
        }

        optionsMap.set(col.id, {
          value: col.id,
          label,
          group,
        });
        columnIndex += 1;
      }

      // Add subline filter option if column has subline and sublineFilterVariant
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter) &&
        col.id
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = col.filterLabel ?? col.header;
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const group = col.meta?.group ?? null;
        const groupName = group ?? DEFAULT_FILTER_GROUP_NAME;

        // Track the first occurrence of each group (use the same index as parent column)
        if (!groupOrderMap.has(groupName)) {
          groupOrderMap.set(groupName, columnIndex - 1);
        }

        optionsMap.set(sublineId, {
          value: sublineId,
          label,
          group,
        });
      }
    }
    return { options: Array.from(optionsMap.values()), groupOrder: groupOrderMap };
  }, [columns]);

  const { options: filterOptions, groupOrder } = filterOptionsResult;

  const handleFiltersChange = useCallback(
    (id: string, value: FilterValue) => {
      let newFilters = [];
      const prevFilter = filtersState?.find((col) => col.id === id);
      if (prevFilter) {
        newFilters =
          filtersState?.map((col) => {
            if (col.id === id) {
              return {
                ...col,
                value,
              };
            }
            return { ...col };
          }) ?? [];
      } else {
        newFilters = [...(filtersState ?? []), { id, value }];
      }
      onFiltersChange(newFilters);
    },
    [filtersState, onFiltersChange],
  );

  const handleSelectedFiltersChange = useCallback(
    (ids: string[]) => {
      function initValForVariant(variant?: FilterVariant): unknown {
        if (variant) {
          switch (variant) {
            case 'select':
              return null;
            case 'multi-select':
              return [];
            case 'text':
              return '';
            case 'number-range':
            case 'date-range':
              return [null, null];
            case 'number':
              return null;
            default:
              return null;
          }
        }
        return null;
      }

      // Find newly added filters
      const currentFilterIds = new Set(filtersState?.map((filter) => filter.id) || []);
      const newlyAddedFilters = ids.filter((id) => !currentFilterIds.has(id));

      const newFilters = [];
      for (const id of ids) {
        const prevFilter = filtersState?.find((filter) => filter.id === id);
        if (prevFilter) {
          newFilters.push(prevFilter);
        } else {
          const newFilter = filtersMap.get(id);
          if (newFilter) {
            newFilters.push({
              id,
              value: initValForVariant(newFilter?.variant),
            });
          }
        }
      }

      // Automatically open newly added filters
      if (newlyAddedFilters.length > 0) {
        setOpenFilters((prev) => new Set([...prev, ...newlyAddedFilters]));
      }

      onFiltersChange(newFilters);
    },
    [filtersMap, filtersState, onFiltersChange],
  );

  const handleClearFilters = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onFiltersChange([]);
      if (onClearFilters) {
        onClearFilters(e);
      }
    },
    [onFiltersChange, onClearFilters],
  );

  const handleClearFilter = useCallback(
    (id: string) => {
      const filteredIds =
        filtersState?.filter((filter) => filter.id !== id).map((filter) => filter.id) ?? [];
      handleSelectedFiltersChange(filteredIds);
    },
    [filtersState, handleSelectedFiltersChange],
  );

  const handleFilterOpenChange = useCallback((id: string, isOpen: boolean) => {
    setOpenFilters((prev) => {
      const newSet = new Set(prev);
      if (isOpen) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  const handleFilterValueSelected = useCallback((id: string) => {
    setOpenFilters((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const hasViews = Boolean(staticFilters);
  const hasColumns = Boolean(columns?.length !== 0);
  const hasFilters = Boolean(hasColumns && filtersState?.length > 0);
  const hasControls = Boolean(controls || hasFilters);

  const [ref, { width }] = useMeasure();
  const isNarrow = Boolean(width && width < 700);

  return (
    <TableFiltersContainer ref={ref} isNarrow={isNarrow}>
      {hasViews && (
        <TableFiltersViews relation="separate" size="small">
          {staticFilters}
          {hasColumns && (
            <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
          )}
        </TableFiltersViews>
      )}
      {hasColumns && filters.length > 0 && (
        <TableFiltersOptions>
          <TableFiltersOptionsScroll
            isNarrow={isNarrow}
            hasViews={hasViews}
            hasControls={hasControls}
            ref={optionsRef}
            onScroll={onScrollHandler}
            {...getBoxShadow}
          >
            {filtersState.map((filter) => {
              const mappedFilter = filtersMap.get(filter.id);
              const options: FilterOption[] | undefined =
                mappedFilter && typeof mappedFilter.options === 'function'
                  ? mappedFilter.options(data)
                  : (mappedFilter?.options as FilterOption[] | undefined);

              if (mappedFilter) {
                return (
                  <TableFiltersToken
                    id={mappedFilter.id}
                    key={mappedFilter.id}
                    handle={mappedFilter.label}
                    value={<DisplayForFilterValue value={mappedFilter.value} options={options} />}
                    changeable
                    onTokenClear={() => handleClearFilter(mappedFilter.id)}
                    modified={isFilterApplied(mappedFilter.value)}
                    isOpen={openFilters.has(mappedFilter.id)}
                    onOpenChange={(isOpen) => handleFilterOpenChange(mappedFilter.id, isOpen)}
                  >
                    <TableFilter
                      mappedFilter={mappedFilter}
                      options={options!}
                      handleFiltersChange={handleFiltersChange}
                      handleFilterValueSelected={handleFilterValueSelected}
                    />
                  </TableFiltersToken>
                );
              }
              return null;
            })}
            {filtersState.length < filterOptions.length && (
              <SelectedFiltersDropdown
                options={filterOptions}
                selectedFilters={filtersState.map((filter) => filter.id)}
                setSelectedFilters={handleSelectedFiltersChange}
                groupOrder={groupOrder}
              />
            )}
          </TableFiltersOptionsScroll>
        </TableFiltersOptions>
      )}
      {hasControls && (
        <TableFiltersControls size="small" relation="separate">
          {hasFilters && !isNarrow && (
            <Button
              onClick={handleClearFilters}
              icon="trash-can"
              arrangement="hidden-label"
              appearance="muted"
              size="small"
              variant="destructive"
            >
              Clear filters
            </Button>
          )}
          {hasFilters && controls && !isNarrow && (
            <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
          )}
          {controls && controls}
        </TableFiltersControls>
      )}
    </TableFiltersContainer>
  );
}

// Enhanced interface for TableState integration
interface TableFiltersEnhancedProps<RowType, C extends SharedColumnDef<RowType, any>> {
  columns?: C[];
  data?: RowType[];
  tableState: AutoCollectionStateInterface<RowType, C['id']>;
  controls?: ReactNode;
  staticFilters?: ReactNode;
  multiSelectActions?: ReactNode;
  hideGlobalFilter?: boolean;
  /** When true, column reordering is disabled (no drag handles in Columns dropdown). */
  disableColumnOrder?: boolean;
  /** Shortcut priority for search (0 = ⌘F, 1 = ⌘G). Lower = primary. */
  shortcutPriority?: number;
}

function normalizeFilterOption(option: FilterOption | string): NormalizedFilterOption {
  if (typeof option === 'string') {
    return {
      value: option,
      label: option,
    };
  }

  if (!option.label) {
    return {
      ...option,
      label: option.value,
    };
  }

  return option as NormalizedFilterOption;
}

function normalizeFilterOptions<RowType>(
  data: RowType[] | undefined,
  filterOptions: FilterOptions<RowType> | undefined,
): NormalizedFilterOption[] | undefined {
  if (!filterOptions) {
    return undefined;
  }

  if (Array.isArray(filterOptions)) {
    return filterOptions.map(normalizeFilterOption);
  }

  const options = filterOptions(data)?.map(normalizeFilterOption) ?? [];
  const map = new Map<string, NormalizedFilterOption>();
  for (const option of options) {
    if (!map.has(option.value)) {
      map.set(option.value, option);
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    const aKey = typeof a.label === 'string' ? a.label : a.value;
    const bKey = typeof b.label === 'string' ? b.label : b.value;
    return sortingCollator.compare(aKey, bKey);
  });
}

// Enhanced component that works with TableState
export function TableFiltersEnhanced<RowType, C extends SharedColumnDef<RowType, any>>({
  columns = [],
  data,
  tableState,
  controls,
  staticFilters,
  multiSelectActions,
  hideGlobalFilter,
  disableColumnOrder = false,
  shortcutPriority,
}: TableFiltersEnhancedProps<RowType, C>) {
  const { getBoxShadow, onScrollHandler, handleTargetChange } = useHorizontalShadowOnScroll('both');
  const optionsRef = useRef<HTMLDivElement>(null);
  const [openFilters, setOpenFilters] = useState<Set<string>>(new Set());

  const handleResizeOptions = useCallback(() => {
    if (optionsRef.current) {
      handleTargetChange(optionsRef.current);
    }
  }, [handleTargetChange]);

  useEffect(() => {
    handleResizeOptions();
    window.addEventListener('resize', handleResizeOptions);
    return () => {
      window.removeEventListener('resize', handleResizeOptions);
    };
  }, [handleResizeOptions]);

  const filters: FilterMapVal<RowType>[] = useMemo(() => {
    const filterList: FilterMapVal<RowType>[] = [];

    for (const col of columns) {
      // Add main column filter if it has filter configuration
      if (col && (col.filterFn || col.filter) && col.filterVariant) {
        const label = (col.filterLabel ?? col.header ?? '').toString();
        const value = tableState.columnFilters?.find((filter) => filter.id === col.id)
          ?.value as FilterValue;

        filterList.push({
          id: col.id,
          value,
          label,
          data,
          filter: col.filter,
          options: col.filterOptions,
          variant: col.filterVariant,
        });
      }

      // Add subline filter if column has subline and sublineFilterVariant is defined
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter)
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = col.filterLabel ?? col.header;
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const value = tableState.columnFilters?.find((filter) => filter.id === sublineId)
          ?.value as FilterValue;

        filterList.push({
          id: sublineId,
          value,
          label,
          data,
          filter: col.sublineFilter,
          options: col.sublineFilterOptions,
          variant: col.sublineFilterVariant,
        });
      }
    }

    return filterList;
  }, [columns, tableState.columnFilters, data]);

  const filtersMap = useMemo(() => {
    const map = new Map<string, FilterMapVal<RowType>>();
    for (const f of filters) {
      map.set(f.id, f);
    }
    return map;
  }, [filters]);

  const filterOptionsResult = useMemo(() => {
    const optionsMap = new Map<string, { value: string; label: string; group?: string | null }>();
    const groupOrderMap = new Map<string, number>();
    let columnIndex = 0;

    for (const col of columns) {
      let mainColumnAdded = false;

      // Add main column filter option
      if ((col?.filterFn || col?.filter) && col?.filterVariant && col.id) {
        const label = (col.filterLabel ?? col.header) as string;
        const group = col.meta?.group ?? null;
        const groupName = group ?? DEFAULT_FILTER_GROUP_NAME;

        // Track the first occurrence of each group
        if (!groupOrderMap.has(groupName)) {
          groupOrderMap.set(groupName, columnIndex);
        }

        optionsMap.set(col.id, {
          value: col.id,
          label,
          group,
        });
        columnIndex += 1;
        mainColumnAdded = true;
      }

      // Add subline filter option if column has subline and sublineFilterVariant
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter) &&
        col.id
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = col.filterLabel ?? col.header;
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const group = col.meta?.group ?? null;
        const groupName = group ?? DEFAULT_FILTER_GROUP_NAME;

        // Track the first occurrence of each group
        // If main column was added, use its index; otherwise use current columnIndex
        if (!groupOrderMap.has(groupName)) {
          groupOrderMap.set(groupName, mainColumnAdded ? columnIndex - 1 : columnIndex);
        }

        optionsMap.set(sublineId, {
          value: sublineId,
          label,
          group,
        });
      }
    }
    return { options: Array.from(optionsMap.values()), groupOrder: groupOrderMap };
  }, [columns]);

  const { options: filterOptions, groupOrder } = filterOptionsResult;

  const handleFiltersChange = useCallback(
    (id: string, value: FilterValue) => {
      let newFilters = [];
      const prevFilter = tableState.columnFilters?.find((col) => col.id === id);
      if (prevFilter) {
        newFilters =
          tableState.columnFilters?.map((col) => {
            if (col.id === id) {
              return {
                ...col,
                value,
              };
            }
            return { ...col };
          }) ?? [];
      } else {
        newFilters = [...(tableState.columnFilters ?? []), { id, value }];
      }
      tableState.setColumnFilters(newFilters);
    },
    [tableState],
  );

  const handleSelectedFiltersChange = useCallback(
    (ids: string[]) => {
      function initValForVariant(variant?: FilterVariant): unknown {
        if (variant) {
          switch (variant) {
            case 'select':
              return null;
            case 'multi-select':
              return [];
            case 'text':
              return '';
            case 'number-range':
            case 'date-range':
              return [null, null];
            case 'number':
              return null;
            default:
              return null;
          }
        }
        return null;
      }

      // Find newly added filters
      const currentFilterIds = new Set(tableState.columnFilters?.map((filter) => filter.id) || []);
      const newlyAddedFilters = ids.filter((id) => !currentFilterIds.has(id));

      const newFilters = [];
      for (const id of ids) {
        const prevFilter = tableState.columnFilters?.find((filter) => filter.id === id);
        if (prevFilter) {
          newFilters.push(prevFilter);
        } else {
          const newFilter = filtersMap.get(id);
          if (newFilter) {
            newFilters.push({
              id,
              value: initValForVariant(newFilter?.variant),
            });
          }
        }
      }

      // Automatically open newly added filters
      if (newlyAddedFilters.length > 0) {
        setOpenFilters((prev) => new Set([...prev, ...newlyAddedFilters]));
      }

      tableState.setColumnFilters(newFilters);
    },
    [filtersMap, tableState],
  );

  const handleClearFilters = useCallback(() => {
    tableState.setColumnFilters([]);
  }, [tableState]);

  const handleClearFilter = useCallback(
    (id: string) => {
      const filteredIds =
        tableState.columnFilters?.filter((filter) => filter.id !== id).map((filter) => filter.id) ??
        [];
      handleSelectedFiltersChange(filteredIds);
    },
    [tableState, handleSelectedFiltersChange],
  );

  const handleFilterOpenChange = useCallback((id: string, isOpen: boolean) => {
    setOpenFilters((prev) => {
      const newSet = new Set(prev);
      if (isOpen) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  const hasColumns = Boolean(columns?.length !== 0);
  const hasFilters = Boolean(hasColumns && tableState.columnFilters?.length > 0);
  const hasControls = Boolean(controls || hasFilters);
  const [ref, { width }] = useMeasure();

  const isNarrow = Boolean(width && width < 700);

  const columnsForTableColumns = useMemo(
    () =>
      disableColumnOrder && columns?.length
        ? columns.map((col) => ({
            ...col,
            meta: {
              ...col.meta,
              fixedPosition: col.meta?.fixedPosition ?? ('as-in' as const),
            },
          }))
        : columns,
    [columns, disableColumnOrder],
  );

  return (
    <TableFiltersContainer ref={ref} isNarrow={isNarrow}>
      <TableFiltersViews relation="separate" size="small">
        {multiSelectActions && multiSelectActions}
        <TableColumnsEnhanced<RowType> columns={columnsForTableColumns} tableState={tableState} />
        {staticFilters && staticFilters}
        {hasColumns && filters.length > 0 && !isNarrow && (
          <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
        )}
        {filters.length > 0 && tableState.columnFilters.length === 0 && (
          <SelectedFiltersDropdown
            options={filterOptions}
            selectedFilters={tableState.columnFilters.map((filter) => filter.id)}
            setSelectedFilters={handleSelectedFiltersChange}
            groupOrder={groupOrder}
          />
        )}
      </TableFiltersViews>
      {hasColumns && filters.length > 0 && tableState.columnFilters.length > 0 && (
        <TableFiltersOptions>
          <TableFiltersOptionsScroll
            isNarrow={isNarrow}
            hasViews
            hasControls={hasControls}
            ref={optionsRef}
            onScroll={onScrollHandler}
            {...getBoxShadow}
          >
            {tableState.columnFilters.map((filter) => {
              const mappedFilter = filtersMap.get(filter.id);
              const options = normalizeFilterOptions(
                data ?? tableState.tableRef?.current?.data,
                mappedFilter?.options,
              );

              if (mappedFilter) {
                return (
                  <TableFiltersToken
                    id={mappedFilter.id}
                    key={mappedFilter.id}
                    handle={mappedFilter.label}
                    value={<DisplayForFilterValue value={mappedFilter.value} options={options} />}
                    changeable
                    onTokenClear={() => handleClearFilter(mappedFilter.id)}
                    modified={isFilterApplied(mappedFilter.value)}
                    isOpen={openFilters.has(mappedFilter.id)}
                    onOpenChange={(isOpen) => handleFilterOpenChange(mappedFilter.id, isOpen)}
                  >
                    <TableFilter
                      mappedFilter={mappedFilter}
                      options={options}
                      handleFiltersChange={handleFiltersChange}
                    />
                  </TableFiltersToken>
                );
              }
              return null;
            })}
            {tableState.columnFilters.length < filterOptions.length && (
              <SelectedFiltersDropdown
                options={filterOptions}
                selectedFilters={tableState.columnFilters.map((filter) => filter.id)}
                setSelectedFilters={handleSelectedFiltersChange}
                groupOrder={groupOrder}
              />
            )}
            {hasFilters && isNarrow && (
              <Button
                onClick={handleClearFilters}
                arrangement="hidden-label"
                appearance="muted"
                icon="trash-can"
                size="small"
                variant="destructive"
              >
                Clear filters
              </Button>
            )}
          </TableFiltersOptionsScroll>
        </TableFiltersOptions>
      )}
      <TableFiltersControls size="small" relation="separate">
        {hasFilters && !isNarrow && (
          <Button
            onClick={handleClearFilters}
            arrangement="hidden-label"
            appearance="muted"
            icon="trash-can"
            size="small"
            variant="destructive"
          >
            Clear filters
          </Button>
        )}
        {hasFilters && controls && !isNarrow && (
          <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
        )}
        {controls && controls}
        {!hideGlobalFilter && (
          <KeyBoundSearchInput
            searchParamStateKey={`${tableState.storageKey}-filter`}
            shortcutPriority={shortcutPriority}
            aria-label="Filter"
            value={tableState.globalFilter}
            onChange={tableState.setGlobalFilter}
          />
        )}
      </TableFiltersControls>
    </TableFiltersContainer>
  );
}
